import { useCallback, useMemo } from "react";
import { useQueries } from "react-query";
import type { UseQueryResult } from "react-query";
import i18next from "i18next";

import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { useCompanyOverviewContext } from "src/context/Company/Overview/useCompanyOverviewContext";

import { useGetAllPuckPages } from "src/api/client-api/cms/useGetAllPuckPages";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { PuckPage } from "src/api/client-api/cms/useCreatePuckPage";
import { PuckPageResponseSchema } from "src/api/client-api/cms/useGetPuckPage";

type VisiblePublication = {
    page: PuckPage;
    scoreTitle: string;
    scoreIdForExport: number | null;
    isAvailable: boolean;
    isSsqValidated: boolean;
    downloadOnlyOnSsqValidated: boolean;
};

type PageVisibilityContext = {
    url: string;
    page: PuckPage;
    scoreTitle: string;
    scoreIdForExport: number | null;
    isAvailable: boolean;
    isSsqValidated: boolean;
    downloadOnlyOnSsqValidated: boolean;
};

const normalizeStringList = (value: unknown): string[] => {
    if (Array.isArray(value)) {
        return value.filter((item): item is string => typeof item === "string" && item.trim() !== "");
    }
    if (typeof value === "string") {
        const v = value.trim();
        return v === "" ? [] : [v];
    }
    return [];
};

const parseRootProps = (page: PuckPage): Record<string, unknown> => {
    try {
        const parsedContent: unknown = page.content ? JSON.parse(page.content) : {};
        if (!parsedContent || typeof parsedContent !== "object") return {};
        const rootObject = (parsedContent as { root?: unknown }).root;
        if (!rootObject || typeof rootObject !== "object") return {};
        const rootProps = (rootObject as { props?: unknown }).props;
        return rootProps && typeof rootProps === "object" ? (rootProps as Record<string, unknown>) : {};
    } catch {
        return {};
    }
};

const getDownloadOnlyOnSsqValidated = (rootProps: Record<string, unknown>): boolean => {
    const value = rootProps.downloadOnlyOnSsqValidated;

    if (typeof value === "boolean") return value;
    if (typeof value === "string") return value !== "false";
    if (typeof value === "number") return value !== 0;

    return true;
};

type UseMyPublicationsDataOptions = {
    scoreIdsFilter?: number[];
};

export const buildPublicationExportUrl = (
    url: string,
    companyId: number,
    scoreId: number | null,
    campaignId?: number,
): string => {
    const targetUrl = new URL(url, window.location.origin);
    targetUrl.searchParams.set("selected_entity_id", String(companyId));
    targetUrl.searchParams.set("selected_organization_id", String(companyId));

    if (typeof scoreId === "number") {
        targetUrl.searchParams.set("score_id", String(scoreId));
    }

    if (typeof campaignId === "number") {
        targetUrl.searchParams.set("campaign_id", String(campaignId));
    }
    targetUrl.searchParams.set("entity_type", "company");

    return `${targetUrl.pathname}${targetUrl.search}`;
};

export const useMyPublicationsData = (options: UseMyPublicationsDataOptions = {}) => {
    const { company } = useCompanyMetadata();
    const { currentCampaign } = useFoodpilotContext();
    const { overview } = useCompanyOverviewContext();

    const scoreIdsFilter = useMemo(() => {
        if (!Array.isArray(options.scoreIdsFilter)) return undefined;
        const scoreIdentifiers = options.scoreIdsFilter.filter(
            (scoreId): scoreId is number => typeof scoreId === "number" && !Number.isNaN(scoreId),
        );
        return scoreIdentifiers.length > 0 ? scoreIdentifiers : undefined;
    }, [options.scoreIdsFilter]);

    const companyScores = overview?.scores ?? [];

    const companyScoreIds = useMemo(() => {
        return companyScores
            .map((companyScore) => companyScore.id)
            .filter((scoreId): scoreId is number => typeof scoreId === "number");
    }, [companyScores]);

    const effectiveCompanyScoreIds = useMemo(() => {
        if (!scoreIdsFilter) return companyScoreIds;
        return companyScoreIds.filter((id) => scoreIdsFilter.includes(id));
    }, [companyScoreIds, scoreIdsFilter]);

    const scoreTitleById = useMemo(() => {
        const scoreTitleByScoreId: Record<number, string> = {};
        companyScores.forEach((companyScore) => {
            if (typeof companyScore.id === "number" && typeof companyScore.title === "string") {
                scoreTitleByScoreId[companyScore.id] = companyScore.title;
            }
        });
        return scoreTitleByScoreId;
    }, [companyScores]);

    const isLockedByScoreId = useMemo(() => {
        const ssqLockedById: Record<number, boolean> = {};
        (overview?.ssqs ?? []).forEach((questionnaire) => {
            if (typeof questionnaire.id === "number") {
                ssqLockedById[questionnaire.id] = questionnaire.post?.isLocked === true;
            }
        });

        const lockedByScoreId: Record<number, boolean> = {};
        companyScores.forEach((companyScore) => {
            const questionnaireId = companyScore.ssq?.id;
            if (typeof companyScore.id !== "number" || typeof questionnaireId !== "number") return;
            lockedByScoreId[companyScore.id] = ssqLockedById[questionnaireId] === true;
        });

        return lockedByScoreId;
    }, [companyScores, overview?.ssqs]);

    const {
        pages,
        isLoading: isPagesLoading,
        isError: isPagesError,
    } = useGetAllPuckPages({
        page: 1,
        itemsPerPage: 200,
        pageType: "PDF",
        status: "active",
    });

    const pagesForVisibility = useMemo<PuckPage[]>(() => {
        return pages.filter((puckPage: PuckPage) => typeof puckPage.url === "string" && puckPage.url.trim() !== "");
    }, [pages]);

    const lang = i18next.language;

    const pageContexts = useMemo<PageVisibilityContext[]>(() => {
        if (effectiveCompanyScoreIds.length === 0) {
            return [];
        }

        return pagesForVisibility.flatMap((page: PuckPage): PageVisibilityContext[] => {
            const url = page.url as string;
            const rootProps = parseRootProps(page);
            const downloadOnlyOnSsqValidated = getDownloadOnlyOnSsqValidated(rootProps);
            const restrictedScoreIds = normalizeStringList(rootProps.score)
                .map((scoreIdAsString) => Number(scoreIdAsString))
                .filter((scoreIdAsNumber) => !Number.isNaN(scoreIdAsNumber));

            const candidateScoreIds =
                restrictedScoreIds.length === 0 ?
                    effectiveCompanyScoreIds
                :   restrictedScoreIds.filter((id) => effectiveCompanyScoreIds.includes(id));

            if (candidateScoreIds.length === 0) {
                return [];
            }

            const preferredScoreId =
                candidateScoreIds.length === 1 ?
                    candidateScoreIds[0]
                :   (candidateScoreIds.find((id) => isLockedByScoreId[id] === true) ?? candidateScoreIds[0] ?? null);

            const isSsqValidated = preferredScoreId !== null ? isLockedByScoreId[preferredScoreId] === true : false;
            const isAvailable = downloadOnlyOnSsqValidated ? isSsqValidated : true;

            const scoreTitle =
                restrictedScoreIds.length === 0 ?
                    candidateScoreIds.map((id) => scoreTitleById[id] ?? String(id)).join(", ")
                :   restrictedScoreIds.map((id) => scoreTitleById[id] ?? String(id)).join(", ");

            return [
                {
                    url,
                    page,
                    scoreTitle,
                    scoreIdForExport: preferredScoreId,
                    isAvailable,
                    isSsqValidated,
                    downloadOnlyOnSsqValidated,
                },
            ];
        });
    }, [effectiveCompanyScoreIds, isLockedByScoreId, pagesForVisibility, scoreTitleById]);

    const visibilityQueries = useQueries(
        pageContexts.map((pageContext) => {
            const url = pageContext.url;
            const normalizedUrl = url.startsWith("/") ? url : `/${url}`;

            const requestQueryParameters: Record<string, string> = {
                selected_entity_id: String(company.id),
                selected_organization_id: String(company.id),
                entity_type: "company",
            };

            if (typeof pageContext.scoreIdForExport === "number") {
                requestQueryParameters.score_id = String(pageContext.scoreIdForExport);
            }

            return {
                queryKey: ["myPublicationsVisibility", url, company.id, pageContext.scoreIdForExport ?? null, lang],
                enabled: url.trim() !== "",
                retry: false,
                queryFn: async (): Promise<PuckPage | null> => {
                    try {
                        const endpoint = `/api/cms/puck/page-by-url/${normalizedUrl}`;
                        const response = await loggedInApiCall.get<PuckPage>(endpoint, {
                            params: requestQueryParameters,
                        });
                        return PuckPageResponseSchema.parse(response.data);
                    } catch {
                        return null;
                    }
                },
            };
        }),
    ) as UseQueryResult<PuckPage | null>[];

    const isVisibilityLoading = visibilityQueries.some((q) => q.isLoading);

    const visiblePublications = useMemo<VisiblePublication[]>(() => {
        return pageContexts
            .map((pageContext: PageVisibilityContext, index: number): VisiblePublication | null => {
                const page = visibilityQueries[index]?.data ?? null;
                if (page === null) {
                    return null;
                }
                return {
                    page,
                    scoreTitle: pageContext.scoreTitle,
                    scoreIdForExport: pageContext.scoreIdForExport,
                    isAvailable: pageContext.isAvailable,
                    isSsqValidated: pageContext.isSsqValidated,
                    downloadOnlyOnSsqValidated: pageContext.downloadOnlyOnSsqValidated,
                };
            })
            .filter(
                (publication: VisiblePublication | null): publication is VisiblePublication => publication !== null,
            );
    }, [pageContexts, visibilityQueries]);

    const availableCount = useMemo(
        () => visiblePublications.filter((publication: VisiblePublication) => publication.isAvailable).length,
        [visiblePublications],
    );

    const notAvailableCount = useMemo(
        () => visiblePublications.filter((publication: VisiblePublication) => !publication.isAvailable).length,
        [visiblePublications],
    );

    const buildExportUrl = useCallback(
        (url: string, scoreId: number | null): string =>
            buildPublicationExportUrl(url, company.id, scoreId, currentCampaign?.id),
        [company.id, currentCampaign?.id],
    );

    const generateFilename = useCallback((path: string): string => {
        const cleanPath = path.replace(/^\/+|\/+$/g, "").replace(/\//g, "-");
        const filename = cleanPath || "export";
        return `${filename}-${new Date().toISOString().split("T")[0]}.pdf`;
    }, []);

    return {
        hasTemplates: pagesForVisibility.length > 0,
        isPagesLoading,
        isPagesError,
        isVisibilityLoading,
        visiblePublications,
        availableCount,
        notAvailableCount,
        buildExportUrl,
        generateFilename,
    };
};
